import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  effect,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  ReactiveFormsModule,
  FormControl,
  ValidationErrors,
} from '@angular/forms';
import { FormStep } from '@pages/home/enums/form-step.enum';
import { Field, FormConfig } from '@pages/home/interfaces/config.interface';
import { DateMaskDirective } from '@shared/core/directives/date-mask.directive';
import { ButtonAppearance } from '@shared/core/enums/button-appearance.enum';
import { ButtonType } from '@shared/core/enums/button-type.enum';
import { FieldType } from '@shared/core/enums/field-type.enum';
import { Button } from '@shared/core/interfaces/button.interface';
import { ButtonComponent } from '@shared/core/modules/button/button.component';
import { DatepickerComponent } from '@shared/core/modules/datepicker/datepicker.component';
import { DropdownListComponent } from '@shared/core/modules/dropdown-list/dropdown-list.component';
import { ModalComponent } from '@shared/core/modules/modal/modal.component';
import { FormDataService } from '@shared/core/services/form-data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { PaymentLeft } from '@pages/home/enums/payments-left.enum';
import { FloatLabelModule } from 'primeng/floatlabel';
import { depositValidator } from '@shared/core/validators/deposit.validator';
import { TooltipComponent } from '@shared/core/modules/tooltip/tooltip.component';
import { DialogService } from 'primeng/dynamicdialog';
import { InfoModalComponent } from '@shared/core/modules/info-modal/info-modal.component';
import { InfoModal } from '@shared/core/enums/info-modal-template.enum';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ToastSeverity } from '@shared/core/enums/toast-severity.enum';
import {
  distinctUntilChanged, map, startWith, tap, withLatestFrom,
} from 'rxjs';
import { ShouldResetHomeForm } from '@shared/core/enums/form-reset.enum';

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgFor,
    NgTemplateOutlet,
    ButtonComponent,
    DateMaskDirective,
    ModalComponent,
    DatepickerComponent,
    DropdownListComponent,
    NgxMaskDirective,
    DropdownModule,
    FloatLabelModule,
    TooltipComponent,
    ToastModule,
  ],
  providers: [DateMaskDirective, provideNgxMask(), DialogService, MessageService],
  templateUrl: './form-builder.component.html',
  styleUrl: './form-builder.component.scss',
})
export class FormBuilderComponent implements OnInit, OnChanges {
  @Input() config!: FormConfig;

  public readonly weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  protected form!: FormGroup;

  protected FIELD_TYPE = FieldType;

  protected buttonData: Button = {
    type: ButtonType.BUTTON,
    text: 'next',
    appearance: ButtonAppearance.PRIMARY,
    disabled: true,
  };

  protected FORM_STEP = FormStep;

  private fb = inject(FormBuilder);

  private formS = inject(FormDataService);

  private destroyRef = inject(DestroyRef);

  private cdRef = inject(ChangeDetectorRef);

  public isPaymentCompleteCtrl:FormControl<{ name:PaymentLeft }> | null = null;

  public isPaymentCompleteData = [
    { name: PaymentLeft.YES },
    { name: PaymentLeft.NO },
  ];

  disableDepositCtrls = false;

  isPaymentCompleteTooltip = 'Please select “yes” if you have any significant payments to be made toward your trip. Select “no” if your trip is paid in full.';

  private readonly dialogService = inject(DialogService);

  private readonly messageService = inject(MessageService);

  constructor() {
    effect(() => {
      if (this.formS.resetHomePageFormSignal() === ShouldResetHomeForm.YES) {
        this.initializeForm();
        this.observeFormChanges();
        this.handleStepDepositStep();
      }
    });
  }

  ngOnInit() {
    this.observeFormChanges();

    this.handleStepDepositStep();
  }

  ngOnChanges(): void {
    this.initializeForm();
    this.patchForm();
  }

  get formArray() {
    return this.form.controls['travelers'] as FormArray;
  }

  get isFormInvalid() {
    return this.form.invalid;
  }

  public ctrlValidityTopmost(ctrlName:string) {
    const control = this.form.get(ctrlName);
    return control?.touched && control.invalid;
  }

  public generateTopmostCtrlErrorMessage(ctrlName:string) {
    const control = this.form.get(ctrlName) as FormControl;
    return this.ERROR_MESSAGE(control, control.errors);
  }

  public ctrlValidity(ctrlName: string, formGroupName: number) {
    const control = this.findControlByNameAndFormGroup(ctrlName, formGroupName);

    return control?.touched && control?.invalid;
  }

  public generateTravelerErrorMessage(ctrlName: string, formGroupName: number) {
    const control = this.findControlByNameAndFormGroup(ctrlName, formGroupName) as FormControl;
    if (control && control.invalid) {
      if (control.touched && !control.value) {
        return 'Required field';
      }

      return this.ERROR_MESSAGE(control, control.errors);
    }
    return null;
  }

  public observeFormChanges() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.formS.updateResetState();
        if (this.form.valid) {
          this.formS.updateFormData(val);
        } else {
          this.formS.updateFormInvalid();
        }

        if (this.config.step !== FormStep.Four) {
          this.formS.updateSummaryData(val);
        }
      });
  }

  public addTraveler() {
    const formGroup = this.createFormGroup(this.config?.fields || []);
    this.formArray.push(formGroup);
  }

  public removeTraveler(index: number) {
    this.formArray.removeAt(index);
    this.formArray.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  public navigateToNextStep() {
    const currentStep = this.config?.step;

    this.formS.updateCurrentStep(
      currentStep < this.FORM_STEP.Four ? currentStep + 1 : null,
    );
  }

  public resetForm() {
    this.form.reset();

    if (this.config.step === this.FORM_STEP.Three) {
      this.formS.updateDropdownFilter('');
    }
  }

  private initializeForm() {
    if (this.config?.meta?.isArray) {
      const formArray = this.fb.array([this.createFormGroup(this.config?.fields as Field[])]);
      this.form = this.fb.group({
        travelers: formArray,
      });
    } else {
      this.form = this.createFormGroup(this.config?.fields || []);
    }

    if (this.config.groupLevelValidators) {
      this.form.setValidators(this.config.groupLevelValidators);
      this.form.updateValueAndValidity();
    }

    if (this.config.step === FormStep.Four) {
      this.isPaymentCompleteCtrl = new FormControl(
        this.isPaymentCompleteData[0],
        { nonNullable: true },
      );
    }
  }

  private createFormGroup(fields: Field[]) {
    const formGroup: {
      [key: string]: [string | number | Date, Validators | null];
    } = {};

    fields.forEach((field) => {
      formGroup[field.name] = [field.value, field.validation];
    });

    return this.fb.group(formGroup);
  }

  private findControlByNameAndFormGroup(
    ctrlName: string,
    formGroupName: number,
  ) {
    return this.formArray.at(formGroupName).get(ctrlName);
  }

  private patchForm() {
    const formData = this.findFormData();
    if (formData) {
      Object.entries(formData).forEach(([field, value]) => {
        const control = this.form.get(field);

        if (!Array.isArray(value)) {
          if (control) {
            control?.patchValue(value);
          }
        } else {
          const dates: string[] = [];
          value.forEach((item) => {
            dates.push(item);
          });
          control?.patchValue(dates);

          this.handleTravelerArrayPatch(value);
        }
      });
    }
  }

  private handleTravelerArrayPatch(value: any) {
    if (this.formArray) {
      value.forEach(
        (item: { [s: string]: unknown } | ArrayLike<unknown>, idx: number) => {
          if (idx !== 0) {
            const formGroup: {
              [key: string]: [string | number | Date, Validators | null];
            } = {};
            Object.entries(item).forEach(([key, val]) => {
              formGroup[key] = [val as any, Validators.required];
            });
            this.formArray.push(this.fb.group(formGroup));
          }
        },
      );

      this.formArray.setValue(value);
      this.formArray.updateValueAndValidity();
      this.cdRef.detectChanges();
    }
  }

  private findFormData() {
    return this.formS.formData[this.config.step];
  }

  public updateDepositVisibility(event:DropdownChangeEvent) {
    this.disableDepositCtrls = event.value.name === PaymentLeft.NO;
    this.disableDepositFields(this.disableDepositCtrls, ['payment']);
  }

  private disableDepositFields(disable: boolean, fieldNames: string[]) {
    const dateCtrl = this.form.get('date');
    fieldNames.forEach((fieldName) => {
      const field = this.form.get(fieldName);
      if (field) {
        if (disable) {
          field.disable();
          this.form.setValidators(null);
          field.setValue(null);
          if (dateCtrl?.pristine) {
            this.showToast('Please enter deposit date', 'Info', ToastSeverity.INFO);
          }
        } else {
          field.enable();
          this.form.setValidators(depositValidator());
        }

        this.form.updateValueAndValidity();
        this.cdRef.detectChanges();
      }
    });
  }

  private showExceedsLimitModal() {
    const config = {
      modal: true,
      breakpoints: {
        '600px': '90vw',
      },
      style: { width: '452px' },
      dismissableMask: true,
      data: {
        template: InfoModal.TRIP_COST_EXCEEDS_LIMIT,
      },
    };
    this.dialogService.open(InfoModalComponent, config);
  }

  private ERROR_MESSAGE(control:FormControl, errors:ValidationErrors | null) {
    return Object.entries(errors || []).map(([error, text]) => {
      switch (error) {
        case 'required':
          return 'Required field';
        case 'min':
          return `Must be more than $${text.actual}`;
        case 'tripCostLimit':
          this.showExceedsLimitModal();
          control.setValue('');
          return 'Trip cost exceeds max limit';
        case 'mask':
          return 'Incorrect format';
        default: return null;
      }
    })[0];
  }

  private showToast(
    message: string,
    summary = 'Error occurred',
    severity = ToastSeverity.ERROR,
  ) {
    this.messageService.add({
      severity,
      summary,
      detail: message,
    });
  }

  private handleStepDepositStep() {
    if (this.config.step === FormStep.Four && this.isPaymentCompleteCtrl) {
      this.isPaymentCompleteCtrl.markAsTouched();

      this.disableDepositFields(this.disableDepositCtrls, ['payment']);

      this.form.valueChanges.pipe(
        distinctUntilChanged(),
        withLatestFrom(
          this.isPaymentCompleteCtrl?.valueChanges.pipe(
            distinctUntilChanged(),
            startWith(this.formS.paymentCompleteVal),
            tap((val) => this.formS.updatePaymentComplete(val)),
            map((val) => val.name === PaymentLeft.YES),
          ),
        ),
        tap(([form, paymentLeft]) => {
          this.formS.updateSummaryData(form, {
            paymentLeft,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
        .subscribe();
    }
  }
}
